class AlgoliaUtils {
    constructor() {
        this.class = {
            searchOverlayInput: '.js-gs-overlay-searchinput',
            searchResultsInput: '.js-gs-resultpage-searchinput',
            searchResultsFilter: '.js-gs-searchform--filter-checkbox',
            searchResultsFacets: '.js-gs-filters-list',
            searchResultTiles: '.gs-searchresult-tile',
            searchResultsList: '.js-searchresults-list',
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', AlgoliaUtils.init.bind(this), false);
        } else {
            AlgoliaUtils.init();
        }
    }

    static init() {
        // const searchResultsInput = document.querySelector(this.class.searchResultsInput);
    }

    async getCurrentSearchParams() {
        const searchOverlayInput = document.querySelector(this.class.searchOverlayInput);
        const searchResultsFilter = document.querySelector(this.class.searchResultsFilter);
        const searchResultsFacets = document.querySelector(this.class.searchResultsFacets);
        const checkedFacet = searchResultsFacets ? searchResultsFacets.querySelector('input[type="checkbox"]:checked') : null;
        const facets = [];
        let q = searchOverlayInput ? searchOverlayInput.value.toLowerCase() : '';

        // overlay input is empty on a fresh page load, fall back to the URL so a direct/reloaded search page keeps its query
        if (!q && window.location.search) {
            q = new URLSearchParams(window.location.search).get('q') || '';
        }

        // Check for the current page only filter and add it to the facets array if checked
        if (searchResultsFilter && searchResultsFilter.checked) {
            facets.push('package:' + searchResultsFilter.dataset.currentPageOnly);
        }

        // Check for the checked facet and add it to the facets array if it's not "all"
        if (checkedFacet && checkedFacet.value !== 'all') {
            facets.push('type:' + checkedFacet.value);
        }

        return { q, facets };
    }

    // the checkboxes are the single source of truth for getCurrentSearchParams(), so a
    // facetFilters value from the URL has to be applied to them before the first search
    applyFacetsFromLocation() {
        const facetFilters = new URLSearchParams(window.location.search).get('facetFilters');

        if (!facetFilters) {
            return;
        }

        let facets = [];

        try {
            facets = JSON.parse(facetFilters);
        } catch (error) {
            return;
        }

        if (!Array.isArray(facets)) {
            return;
        }

        const searchResultsFilter = document.querySelector(this.class.searchResultsFilter);
        const searchResultsFacets = document.querySelector(this.class.searchResultsFacets);
        const facetCheckboxes = searchResultsFacets ? Array.from(searchResultsFacets.querySelectorAll('input[type="checkbox"]')) : [];

        facetCheckboxes.forEach((_, index) => {
            facetCheckboxes[index].checked = false;
        });

        facets.forEach((facet) => {
            const separatorIndex = String(facet).indexOf(':');

            if (separatorIndex < 0) {
                return;
            }

            const attribute = String(facet).slice(0, separatorIndex);
            const value = String(facet).slice(separatorIndex + 1);

            if (attribute === 'package' && searchResultsFilter && searchResultsFilter.dataset.currentPageOnly === value) {
                searchResultsFilter.checked = true;
            }

            if (attribute === 'type') {
                const checkbox = facetCheckboxes.find((facetCheckbox) => facetCheckbox.value === value);

                if (checkbox) {
                    checkbox.checked = true;
                }
            }
        });
    }

    // mirrors SearchController::resolveQuery() so queries without any letter/digit
    // (e.g. only backslashes/symbols) never trigger a request that would return all results;
    // also requires at least 3 characters (trimmed) to avoid overly broad searches
    hasRealQuery(value) {
        return String(value).trim().length >= 3 && /\p{L}|\p{N}/u.test(value);
    }

    // falls back to the unfiltered "all" facet, e.g. when the currently selected
    // type facet has no results for a query that changed after the facet was picked
    resetFacetToAll() {
        const searchResultsFacets = document.querySelector(this.class.searchResultsFacets);
        const facetCheckboxes = searchResultsFacets ? Array.from(searchResultsFacets.querySelectorAll('input[type="checkbox"]')) : [];

        facetCheckboxes.forEach((_, index) => {
            facetCheckboxes[index].checked = facetCheckboxes[index].value === 'all';
        });
    }

    getAjaxEndpoint() {
        const overlay = document.querySelector('[data-algolia-endpoint]');

        if (overlay) {
            return window.location.origin + overlay.dataset.algoliaEndpoint;
        }

        return window.location.origin + window.location.pathname.replace(/\/+$/, '');
    }

    // the overlay can be opened from any page, but q/facetFilters/page are only meaningful
    // (and must only be written) on the actual search results page
    isOnSearchResultsPage() {
        return document.querySelector('.js-gs-searchresults') !== null;
    }

    async updateLocationParams(q, facets) {
        if (!this.isOnSearchResultsPage()) {
            return;
        }

        const searchParams = new URLSearchParams(window.location.search);

        searchParams.set('q', q);
        if (facets) {
            searchParams.set('facetFilters', JSON.stringify(facets));
        }
        history.replaceState(null, '', `${window.location.pathname}?${searchParams.toString()}`);
    }

    updatePageParam(page) {
        if (!this.isOnSearchResultsPage()) {
            return;
        }

        const searchParams = new URLSearchParams(window.location.search);

        searchParams.set('page', page);
        history.replaceState(null, '', `${window.location.pathname}?${searchParams.toString()}`);
    }

    async updateSearchInputValues() {
        if (location.search) {
            const searchOverlayInput = document.querySelector(this.class.searchOverlayInput);
            const searchResultsInput = document.querySelector(this.class.searchResultsInput);
            const params = new URLSearchParams(location.search);
            const q = params.get('q');

            if (searchOverlayInput) {
                searchOverlayInput.value = q;
            }

            if (searchResultsInput) {
                searchResultsInput.value = q;
            }
        }
    }

    async checkForCurrentPageOnlyFilter() {
        const currentPageOnlyCheckboxes = document.querySelectorAll('input[type="checkbox"][data-current-page-only]');
        const hasCheckedCurrentPageOnlyFilter = Array.from(currentPageOnlyCheckboxes).some((checkbox) => checkbox.checked);

        return hasCheckedCurrentPageOnlyFilter;
    }

    hideSearchResultTiles() {
        // freeze the wrapper height first, otherwise hiding the tiles collapses the
        // page and the scroll position jumps down before the new results reveal it back up
        const wrapper = document.querySelector(this.class.searchResultsList);
        if (wrapper) {
            wrapper.style.minHeight = `${wrapper.offsetHeight}px`;
        }

        document.querySelectorAll(this.class.searchResultTiles).forEach((tile) => {
            tile.setAttribute('hidden', 'hidden');
        });
    }

    revealSearchResultTiles() {
        document.querySelectorAll(this.class.searchResultTiles).forEach((tile, index) => {
            tile.removeAttribute('hidden');
            tile.style.setProperty('--gs-searchresult-tile-delay', `${index * 35}ms`);
            tile.classList.add('gs-searchresult-tile--revealing');

            tile.addEventListener('animationend', () => {
                tile.classList.remove('gs-searchresult-tile--revealing');
                tile.style.removeProperty('--gs-searchresult-tile-delay');
            }, { once: true });
        });

        const wrapper = document.querySelector(this.class.searchResultsList);
        if (wrapper) {
            wrapper.style.removeProperty('min-height');
        }
    }
}

export default new AlgoliaUtils();
